// API client with real-or-mock fallback.
// If REACT_APP_API_URL is set, we hit the real backend with the Clerk JWT.
// Otherwise, we serve responses from mocks.js so the UI works standalone.
import axios from "axios";
import { mockHandlers, MOCK_LATENCY } from "./mocks";

const API_URL = process.env.REACT_APP_API_URL || "";
export const USE_MOCK = !API_URL;

let tokenGetter = async () => "dev-token";
export function setTokenGetter(fn) {
    tokenGetter = fn;
}

const realClient = axios.create({
    baseURL: API_URL,
    timeout: 130000,
});

realClient.interceptors.request.use(async (config) => {
    try {
        const token = await tokenGetter();
        if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch (e) {
        // no token, that's fine for public endpoints
    }
    config.headers["Accept-Language"] =
        (typeof navigator !== "undefined" && navigator.language) || "en";
    return config;
});

realClient.interceptors.response.use(
    (r) => r,
    (err) => {
        const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Request failed";
        return Promise.reject({
            ...err,
            message: msg,
            status: err?.response?.status,
        });
    },
);

async function mockCall(method, url, body, params) {
    await new Promise((r) => setTimeout(r, MOCK_LATENCY));
    const M = method.toUpperCase();
    const handler = mockHandlers.find((h) => h.match(M, url));
    if (!handler) {
        // eslint-disable-next-line no-console
        console.warn("No mock for", M, url);
        return { data: null };
    }
    const data = handler.run({ url, body, params, method: M });
    if (data && data.__status && data.__status >= 400) {
        const err = new Error(data.error || "Request failed");
        err.status = data.__status;
        throw err;
    }
    return { data };
}

function api(method) {
    return async (url, arg1, arg2) => {
        // arg shapes vary: get(url, {params}) vs post(url, body, {params})
        let body, params;
        if (method === "get" || method === "delete") {
            params = arg1?.params;
        } else {
            body = arg1;
            params = arg2?.params;
        }
        if (USE_MOCK) {
            const res = await mockCall(method, url, body, params);
            return res.data;
        }
        const res = await realClient.request({
            url,
            method,
            data: body,
            params,
        });
        return res.data;
    };
}

export const apiGet = api("get");
export const apiPost = api("post");
export const apiPatch = api("patch");
export const apiPut = api("put");
export const apiDelete = api("delete");

// Endpoint shortcuts
export const api_endpoints = {
    // analysis
    analyse: (body) => apiPost("/api/analyse", body),
    bulkAnalyse: (body) => apiPost("/api/analyse/bulk", body),
    bulkStatus: (jobId) => apiGet(`/api/analyse/bulk/${jobId}`),
    bulkActive: () => apiGet("/api/analyse/bulk/active"),
    // history
    history: (params) => apiGet("/api/history", { params }),
    historyOne: (id) => apiGet(`/api/history/${id}`),
    historyUpdate: (id, body) => apiPatch(`/api/history/${id}`, body),
    historyDelete: (id) => apiDelete(`/api/history/${id}`),
    historyBatch: (ids) =>
        apiGet(`/api/history/batch`, { params: { ids: ids.join(",") } }),
    historyFilterValues: () => apiGet("/api/history/filter-values"),
    // collections
    collections: () => apiGet("/api/collections"),
    createCollection: (name) => apiPost("/api/collections", { name }),
    renameCollection: (id, name) =>
        apiPatch(`/api/collections/${id}`, { name }),
    deleteCollection: (id) => apiDelete(`/api/collections/${id}`),
    // sharing
    shareCollection: (id) => apiPost(`/api/collections/${id}/share`),
    shareStatus: (id) => apiGet(`/api/collections/${id}/share`),
    revokeShare: (id) => apiDelete(`/api/collections/${id}/share`),
    sharedView: (token) => apiGet(`/api/shared/${token}`),
    sharedAnalysis: (token, analysisId) =>
        apiGet(`/api/shared/${token}/${analysisId}`),
    // credits
    creditsBalance: () => apiGet("/api/credits/balance"),
    creditsCheckout: (packageId) =>
        apiPost("/api/credits/checkout", { packageId }),
    sessionStatus: (sessionId) =>
        apiGet("/api/credits/session-status", {
            params: { session_id: sessionId },
        }),
    // profile
    profile: () => apiGet("/api/profile"),
    updateProfile: (body) => apiPut("/api/profile", body),
    deleteProfile: () => apiDelete("/api/profile"),
    exportProfile: () => apiGet("/api/profile/export"),
    referral: () => apiGet("/api/profile/referral"),
    applyReferral: (code) => apiPost("/api/profile/referral", { code }),
    // feedback
    feedback: (body) => apiPost("/api/feedback", body),
};
